import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(undefined);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          sessionStorage.getItem('refreshToken')
        ) {
          originalRequest._retry = true;

          try {
            const refreshToken = sessionStorage.getItem('refreshToken');
            const response = await axios.post(
              `${API}/auth/refresh`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
              }
            );

            const { access_token, refresh_token } = response.data;
            sessionStorage.setItem('accessToken', access_token);
            sessionStorage.setItem('refreshToken', refresh_token);

            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return instance(originalRequest);
          } catch (refreshError) {
            logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      logout();
    }
  }, [api, logout]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token, user_type } = response.data;
    
    sessionStorage.setItem('accessToken', access_token);
    sessionStorage.setItem('refreshToken', refresh_token);
    
    await fetchUser();
    return { ...response.data, user_type };
  }, [api, fetchUser]);

  const register = useCallback(async (email, password, fullName, organizationName, frameworkId = null) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      full_name: fullName,
      organization_name: organizationName,
      framework_id: frameworkId,
    });
    
    const { access_token, refresh_token } = response.data;
    sessionStorage.setItem('accessToken', access_token);
    sessionStorage.setItem('refreshToken', refresh_token);
    
    await fetchUser();
    return response.data;
  }, [api, fetchUser]);

  const forgotPassword = useCallback(async (email) => {
    const response = await api.post('/auth/password/forgot', { email });
    return response.data;
  }, [api]);

  const resetPassword = useCallback(async (token, newPassword) => {
    const response = await api.post('/auth/password/reset', {
      token,
      new_password: newPassword,
    });
    return response.data;
  }, [api]);

  const acceptInvite = useCallback(async (token, password, fullName) => {
    const response = await api.post('/auth/invite/accept', {
      token,
      password,
      full_name: fullName,
    });
    
    const { access_token, refresh_token } = response.data;
    sessionStorage.setItem('accessToken', access_token);
    sessionStorage.setItem('refreshToken', refresh_token);
    
    await fetchUser();
    return response.data;
  }, [api, fetchUser]);

  const setAuthFromTokens = useCallback(async (accessToken, refreshToken, userType) => {
    sessionStorage.setItem('accessToken', accessToken);
    sessionStorage.setItem('refreshToken', refreshToken);
    await fetchUser();
    return { user_type: userType };
  }, [fetchUser]);

  useEffect(() => {
    const initAuth = async () => {
      const token = sessionStorage.getItem('accessToken');
      if (token) {
        await fetchUser();
      }
      setLoading(false);
    };

    initAuth();
  }, [fetchUser]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword,
      acceptInvite,
      setAuthFromTokens,
      api,
    }),
    [user, loading, isAuthenticated, login, register, logout, forgotPassword, resetPassword, acceptInvite, setAuthFromTokens, api]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
